import RoutePathString from '@components/pages/RoutePathString';
import { getLocalizationById } from '@context/useMyRouting/helpers';
import Typography from '@mui/material/Typography';
import { RouteWidgetBaseParameters } from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import { formatPrice } from '@pages/OfferDetail/helpers';
import { Order } from '@services/requests/orderService/interface';
import {
  CalculatedPrice,
  ProductSummaryProps,
  RouteResult,
} from '@services/requests/orderService/types';
import { BranchItem } from '@services/requests/routeCalculator/locationsBranchSearchService/interfaces';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getGroupedTransportLoad } from './helpers';
import RouteSummaryStyledContainer from './StyledComponents';

/**
 * Пропсы компонента
 */
type RouteSummaryProps = {
  order: Order;
  cargo: string[];
  realTimeBaseData: RouteWidgetBaseParameters;
  isUseActualCourses: boolean;
} & ProductSummaryProps &
  CalculatedPrice;

/**
 * Компонент вывода обобщенной информации по маршруту
 */
const RouteSummary = (props: RouteSummaryProps) => {
  const {
    product: { data },
    cargo,
    order,
    orderCurrency,
    fullPriceInTargetCurrency,
    taxInTargetCurrency,
    realTimeBaseData,
    isUseActualCourses,
  } = props;

  const langCode = getLocalizationById(order.language_id).countryCode;

  const { t } = useTranslation(['Common', 'OfferDetail'], { lng: langCode });
  // Получаем данные маршрута
  const route: RouteResult = JSON.parse(data);

  const {
    terminals,
    routePointsItems: { from, to },
  } = route;

  const containerLoads = getGroupedTransportLoad(
    route.route.routes[0].mixedTransport?.containerLoads ?? []
  );
  const truckLoads = getGroupedTransportLoad(
    route.route.routes[0].mixedTransport?.truckLoads ?? []
  );
  const isCrossDockingExist =
    !!route.route.routes[0].mixedTransport && containerLoads.length > 0 && truckLoads.length > 0;

  let points: BranchItem[] = [
    ...(route.route.routes[0].steps
      .map((s) => terminals.find((term) => term?.id === s.startTerminal?.id))
      .filter((s) => !!s) as BranchItem[]),
    to,
  ];

  // Массив 'terminals' может не содержать начального терминала
  if (points[0]?.id !== from?.id && from.type === 'terminal') {
    points = [from, ...points];
  }

  // если отправка идет не из терминала
  if (from.type !== 'terminal') {
    points.unshift(from);
  }

  const dateCalculation = dayjs(order?.date || new Date())
    .locale('ru')
    .format('D.MM.YY');

  const rubleCurrency = route.baseData.currencies.find((c) => c.id === '1');
  const currentCurrency = route.baseData.currencies.find((c) => c.id === orderCurrency);

  const currentCurrencyCourse =
    route.route.routes[0].steps[0].shoulderOfferPriceCondition.currency?.course;
  const orderCurrencyList = route.baseData.currencies
    .filter((c) => c?.id !== rubleCurrency?.id)
    .map((c) => {
      if (!isUseActualCourses) {
        return c;
      }
      return realTimeBaseData.currencies.find((cur) => cur.id === c.id);
    })
    .filter(Boolean)
    .map((c) => (
      <Typography key={c.code} variant={'subtitle1'} component={'div'}>
        <span>
          {c.nominal} {c.code} = {c.course || currentCurrencyCourse} {rubleCurrency?.code || 'RUB'}
        </span>
      </Typography>
    ));

  return (
    <>
      <RouteSummaryStyledContainer className="kendo-pdf--prevent-split">
        <div className="body">
          <Typography variant={'subtitle1'} component={'div'} className="route">
            <RoutePathString value={points} langCode={langCode} order={order} />
          </Typography>
          <ul className="cargo-params">
            {route.type === 'container' && <span>{t('Common:Containers')}: </span>}
            {route.type === 'custom' && <span>{t('Common:Custom')}: </span>}
            {route.type === 'truck' && <span>{t('Common:Trucks')}: </span>}
            {cargo.map((c, i) => (
              <li className={'cargo-params__item'} key={`cargo-params--${i}`}>
                <Typography
                  variant={'subtitle1'}
                  component={'span'}
                  className="cargo-params"
                  key={`cargo-params--${i}`}
                >
                  {c}
                </Typography>
              </li>
            ))}
          </ul>

          {isCrossDockingExist && (
            <div className="tansport-loads">
              <span>{t('Common:CustomTransportTypes.DetailsTitle')}:</span>
              <div className="transport-loads-wrapper">
                <div className="transport-loads-list">
                  <span>{t('Common:Containers')}: </span>
                  {containerLoads.map((loads, index) => {
                    const load = loads[0];
                    const container = realTimeBaseData.containers.find(
                      (container) => container.id === load.transportTypeId.toString()
                    );
                    const { default_name = '?' } = container || {};

                    return (
                      <span key={`container-load-${index}`}>
                        {default_name} ({formatPrice(load.usedWeight, 0)} {t('Common:kg')}) ×{' '}
                        {loads.length}
                      </span>
                    );
                  })}
                </div>
                <div className="transport-loads-list">
                  <span>{t('Common:Trucks')}: </span>
                  {truckLoads.map((loads, index) => {
                    const load = loads[0];
                    const truck = realTimeBaseData.trucks.find(
                      (truck) => truck.id === load.transportTypeId.toString()
                    );
                    const { default_name = '?', localized_names_data = [] } = truck || {};
                    const foundLocalization = localized_names_data.find(
                      (l) => l.lang_id === order.language_id
                    );
                    const name = foundLocalization?.message?.trim() || default_name;

                    return (
                      <span key={`truck-load-${index}`}>
                        {name} ({formatPrice(load.usedWeight, 0)} {t('Common:kg')}) × {loads.length}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <Typography variant={'subtitle1'} component={'div'} className="delivery-time">
            {t('OfferDetail:NetTransitTime')}
            <sup>1</sup>: {route.route.deliveryTime}{' '}
            {route.route.deliveryTime > 1 ? t('Common:Days') : t('Common:Day')}
          </Typography>
        </div>
        <div className="aside">
          <Typography variant={'subtitle1'} component={'div'} className="full-cost">
            {t('Common:Total')}: {formatPrice(fullPriceInTargetCurrency)} {currentCurrency?.code}
          </Typography>
          <Typography variant={'subtitle1'} component={'div'} className="tax">
            {t('OfferDetail:IncludingVAT')}: {formatPrice(taxInTargetCurrency)}{' '}
            {currentCurrency?.code}
          </Typography>
          {!!orderCurrencyList.length && (
            <div className="currency">
              <Typography variant={'subtitle1'} component={'div'}>
                {t('OfferDetail:ExchangeRateOn')} {dateCalculation}:
              </Typography>
              {orderCurrencyList}
            </div>
          )}
        </div>
      </RouteSummaryStyledContainer>
    </>
  );
};

export default RouteSummary;
